import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UploadCloud } from "lucide-react";
import { useState } from "react";
import type {
  AgentPublishFeatureType,
  AgentPublishType,
  AgentPublishVersion,
} from "@/services/agentPublishService";

interface PublishAgentDialogProps {
  assistantName?: string;
  open: boolean;
  isPublishing: boolean;
  onOpenChange: (open: boolean) => void;
  onPublish: (options: {
    type: AgentPublishType;
    featureType: AgentPublishFeatureType;
    version: AgentPublishVersion;
  }) => void;
}

export default function PublishAgentDialog({
  assistantName,
  open,
  isPublishing,
  onOpenChange,
  onPublish,
}: PublishAgentDialogProps) {
  const [type, setType] = useState<AgentPublishType>("voice");
  const [featureType, setFeatureType] =
    useState<AgentPublishFeatureType>("job");
  const [version, setVersion] = useState<AgentPublishVersion>(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg">Publish Assistant</DialogTitle>
          <DialogDescription className="text-sm">
            Choose how "{assistantName || "this assistant"}" should be published.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Type</label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as AgentPublishType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="voice">Voice</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Feature type</label>
            <Select
              value={featureType}
              onValueChange={(value) =>
                setFeatureType(value as AgentPublishFeatureType)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select feature type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="job">Job</SelectItem>
                <SelectItem value="campaign">Campaign</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Version</label>
            <Select
              value={String(version)}
              onValueChange={(value) =>
                setVersion(Number(value) as AgentPublishVersion)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onPublish({ type, featureType, version })}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="mr-2 h-4 w-4" />
            )}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
